'use client'

import { type Project, type Media } from '@/payload-types'
import React, { useState } from 'react'
import { CMSLink } from '@/components/Link'
import RichText from '@/components/RichText'
import { Subheading } from '@/components/ui/subheading'
import { cn } from '@/utilities/ui'
import { PixelatedImage } from '@/components/PixelatedImage'

export const ProjectCard: React.FC<{
  project: Project
  variant?: 'light' | 'dark'
  aspectRatio?: '3/4' | '4/3' | '5/4'
  imageClassName?: string
  pixelSize?: number
  defaultPixelated?: boolean
}> = ({ project, variant = 'dark', aspectRatio = '3/4', imageClassName, pixelSize = 24 }) => {
  const [isHovering, setIsHovering] = useState(false)
  const heroImage =
    project.heroImage && typeof project.heroImage === 'object' ? (project.heroImage as Media) : null

  return (
    <div
      className="group flex flex-col md:flex-row gap-x-[30px]"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="flex-none relative overflow-hidden">
        {heroImage && (
          <PixelatedImage
            src={heroImage.url || ''}
            alt={heroImage.alt || ''}
            aspectRatio={aspectRatio}
            className={cn('w-[120px] mt-[4px] mb-[16px]', imageClassName)}
            pixelSize={pixelSize}
            defaultState="original"
            isHovering={isHovering}
          />
        )}
      </div>
      <div className="flex flex-col items-start gap-[12px]">
        <Subheading size="md" className={cn(variant === 'light' ? 'text-light' : 'text-dark')}>
          {project.title}
        </Subheading>
        <RichText
          className={cn(
            '[&_p]:!text-body-xs max-w-[500px] md:text-balance',
            variant === 'light' ? 'prose-light' : 'prose-dark',
          )}
          data={project.content}
        />
        {project.link && (
          <CMSLink
            url={project.link.url}
            newTab
            variant={variant === 'light' ? 'link-highlighter' : 'link-punch'}
            size="clear"
            className="group-hover:underline"
          >
            Learn more
          </CMSLink>
        )}
      </div>
    </div>
  )
}
